import React, { Component } from "react";
import { Button, Alert } from "reactstrap";
import { Redirect, RouteComponentProps } from "react-router";
import {
  ProviderService,
  SaveTestersConsentService,
  UserService,
} from "../generated";
import { Policy } from "./policy";
import DOMPurify from "dompurify";

export interface State {
  loading: boolean;
  success: boolean;
  url: string;
  name: string;
  provider: string;
  confetti: boolean;
  instruction: string;
  status: string;
  isInvited: boolean;
  isAuthenticated: boolean;
  area: string;
}

export class AccountsGetStarted extends Component<RouteComponentProps, State> {
  constructor(props: RouteComponentProps) {
    super(props);

    this.state = {
      loading: true,
      success: false,
      url: null,
      name: null,
      provider: null,
      confetti: false,
      instruction: null,
      status: "Full",
      isInvited: false,
      isAuthenticated: false,
      area: null,
    };
  }

  async componentDidMount() {
    const isAuthenticated = await UserService.userIsAuthenticated();
    if (isAuthenticated) {
      const isInvited = await UserService.userIsInvited();
      this.setState({
        isInvited,
        isAuthenticated,
      });
    }

    const rawCode = new URLSearchParams(this.props.location.search).get("code");
    const sanitizedCode = DOMPurify.sanitize(rawCode);

    const data = await ProviderService.providerAuthenticate({
      code: sanitizedCode,
    });
    this.setState({
      loading: false,
      success: data.success,
      url: data.url,
      name: data.name,
      instruction: data.instruction,
      provider: data.provider || "your bank",
      status: data.status,
      confetti: data.confetti,
      area: data.area,
    });
  }

  renderInstruction() {
    if (!this.state.instruction) return;

    return (
      <Alert color="primary">
        <strong>Instruction:</strong> {this.state.instruction}
      </Alert>
    );
  }

  warnIfNotFull() {
    if (this.state.status === "Full") return;

    if (this.state.status === "Limited")
      return (
        <Alert color="warning">
          It would seem that {this.state.provider} might be currently
          experiencing some issues.
        </Alert>
      );

    if (this.state.status === "Disabled")
      return (
        <Alert color="danger">
          It would seem that {this.state.provider} is experiencing some severe
          issues at the moment, and is not available for testing.
        </Alert>
      );
  }

  renderConfettiWelcome() {
    return (
      <div>
        <h2>
          Hi {this.state.name || "there!"}{" "}
          <span role="img" aria-label="*wave*">
            👋
          </span>
        </h2>
        <br />
        <p>
          Thank you for helping us testing the connection to{" "}
          {this.state.provider}.
        </p>
        <p>
          Clicking the button below will start your mission, by connecting to{" "}
          {this.state.provider}.
        </p>

        <p>{this.renderPrivacyNotice()}</p>

        {this.warnIfNotFull()}

        {this.renderInstruction()}

        {this.state.status !== "Disabled" && (
          <Button
            onClick={() => (window.location.href = this.state.url)}
            color="primary"
          >
            Let&apos;s go!
          </Button>
        )}
      </div>
    );
  }

  renderBoringWelcome() {
    return (
      <div>
        {this.state.name && (
          <h2>
            Hi {this.state.name}
            <br />
          </h2>
        )}
        <p>
          Thank you for helping us testing the connection to{" "}
          {this.state.provider}.
        </p>
        <p>Please click the button below to start the authentication.</p>

        <p>{this.renderPrivacyNotice()}</p>

        {this.warnIfNotFull()}

        {this.renderInstruction()}

        {this.state.status !== "Disabled" && (
          <Button onClick={() => this.authenticate()} color="primary">
            Authenticate
          </Button>
        )}
      </div>
    );
  }

  async authenticate() {
    const rawCode = new URLSearchParams(this.props.location.search).get("code");
    const sanitizedCode = DOMPurify.sanitize(rawCode);

    await SaveTestersConsentService.SaveTestersConsent({
      missionId: sanitizedCode,
    });
    window.location.href = this.state.url;
  }

  renderPrivacyNotice() {
    return (
      <Policy
        area={this.state.area}
        providerId={this.state.provider}
        buttonPosition="bellow"
      />
    );
  }

  render() {
    const { isAuthenticated, isInvited, loading, success, confetti } =
      this.state;

    if (loading) {
      return <p>Loading, please wait...</p>;
    }

    if (isAuthenticated && isInvited) {
      return <Redirect to={`/onboarding`} />;
    }

    if (success && confetti) {
      return this.renderConfettiWelcome();
    }

    if (success) {
      return this.renderBoringWelcome();
    }

    return (
      <div>
        <h2>Error</h2>
        <br />
        <p>The provided link did not work.</p>
      </div>
    );
  }
}
